// Persistent, reactive app preferences (kiosk zoom + fullscreen intent).
// Preferences are stored in localStorage so they survive a page reload.
import { reactive, watch } from 'vue';

const STORAGE_KEY = 'booth:preferences';

// Zoom bounds mirror Chrome's page-zoom range/step.
export const ZOOM_MIN = 0.5;
export const ZOOM_MAX = 2;
export const ZOOM_STEP = 0.1;

const DEFAULTS = { zoom: 1, fullscreenPreferred: false };

const loadStored = () => {
    try {
        return { ...DEFAULTS, ...JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}') };
    } catch (e) {
        return { ...DEFAULTS };
    }
};

// Single shared reactive store.
export const preferences = reactive(loadStored());

// Persist on any change.
watch(
    preferences,
    (value) => localStorage.setItem(STORAGE_KEY, JSON.stringify(value)),
    { deep: true }
);

const clampZoom = (value) => {
    const rounded = Math.round(value * 100) / 100;
    return Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, rounded));
};

// Expose the zoom factor as `--app-zoom`; the layout scales <body> with
// `transform: scale(var(--app-zoom))` (see main.css), NOT CSS `zoom`.
// `transform` is correctly inverted during hit-testing — including touch — so a
// finger lands where it points at any zoom. CSS `zoom` is not on some touch
// browsers (the kiosk's), which made the touch point drift as zoom dropped.
// vh-based heights still divide by --app-zoom because neither zoom nor transform
// scales viewport units, so the existing compensation is unchanged.
export const applyZoom = () => {
    // Clear any stale inline `zoom` from earlier builds that used it.
    document.documentElement.style.zoom = '';
    document.documentElement.style.setProperty('--app-zoom', preferences.zoom);
};

export const setZoom = (value) => {
    preferences.zoom = clampZoom(value);
    applyZoom();
};

export const zoomIn = () => setZoom(preferences.zoom + ZOOM_STEP);
export const zoomOut = () => setZoom(preferences.zoom - ZOOM_STEP);
export const resetZoom = () => setZoom(1);
export const zoomPercent = () => Math.round(preferences.zoom * 100);

// Apply all persisted preferences to the DOM (call once on app start).
export const applyPreferences = () => {
    applyZoom();
};
